import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Hand, Sparkles, Droplet, Leaf, Shield, Wind, Sun, Moon, Plane, Briefcase,
  Laptop, Dumbbell, Clock, Heart, ShoppingBag, Zap, Coffee, Smile, Waves,
  FlaskConical, Ban, Rabbit, Recycle, Flame, Home, TrainFront, Music,
  Check, ArrowRight, Loader2,
} from "lucide-react";

const ICONS = {
  hands: Hand, sparkles: Sparkles, droplet: Droplet, leaf: Leaf, shield: Shield,
  wind: Wind, sun: Sun, moon: Moon, plane: Plane, briefcase: Briefcase,
  laptop: Laptop, dumbbell: Dumbbell, clock: Clock, heart: Heart, bag: ShoppingBag,
  zap: Zap, coffee: Coffee, smile: Smile, waves: Waves, flask: FlaskConical,
  ban: Ban, rabbit: Rabbit, recycle: Recycle, flame: Flame, home: Home,
  train: TrainFront, music: Music,
};

const MIcon = ({ name, className }) => {
  const C = ICONS[(name || "").toLowerCase()] || Sparkles;
  return <C className={className} strokeWidth={1.5} />;
};

const SectionTitle = ({ children }) => (
  <h3 className="font-heading font-black uppercase text-xl tracking-tight text-[#1a1812]">{children}</h3>
);

export const ConceptMemo = ({ memo, packshotUrl, packshotLoading, createdAt }) => {
  const navigate = useNavigate();
  if (!memo) return null;
  const date = createdAt
    ? new Date(createdAt).toLocaleDateString("en-IN", { month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" });

  return (
    <div className="bg-[#f4f1e8] text-[#1a1812] p-6 md:p-10 font-body select-text" data-testid="concept-memo">
      {/* ===== Header + hero grid ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <div className="font-mono text-[11px] font-bold tracking-[0.3em] uppercase" data-testid="memo-label">Concept Memo</div>
          <h2 className="font-heading font-black uppercase leading-[0.92] mt-3">
            <span className="block text-3xl md:text-4xl" data-testid="memo-brand">{memo.brand}</span>
            <span className="block text-5xl md:text-6xl tracking-tight" data-testid="memo-product-name">{memo.product_name}</span>
          </h2>

          {/* Meta table */}
          <div className="mt-6 border-t border-[#1a1812]/25 text-sm">
            <div className="grid grid-cols-2 gap-4 border-b border-[#1a1812]/25 py-2.5">
              <div><span className="font-bold uppercase text-[11px] tracking-wider">Brand: </span>{memo.brand}</div>
              <div><span className="font-bold uppercase text-[11px] tracking-wider">Category: </span>{memo.category}</div>
            </div>
            <div className="grid grid-cols-2 gap-4 border-b border-[#1a1812]/25 py-2.5">
              <div><span className="font-bold uppercase text-[11px] tracking-wider">Concept date: </span>{date}</div>
              <div><span className="font-bold uppercase text-[11px] tracking-wider">Geography inspiration: </span>{memo.geography_inspiration}</div>
            </div>
          </div>

          {/* WHAT */}
          <div className="mt-7">
            <SectionTitle>What</SectionTitle>
            <p className="text-[15px] leading-relaxed mt-2">{memo.what}</p>
            <p className="text-[15px] font-bold mt-2">{memo.what_bold}</p>
          </div>

          {/* ONE-LINE PITCH */}
          <div className="bg-[#e9e7c4] px-6 py-5 mt-6" data-testid="memo-pitch">
            <div className="font-mono text-[10px] font-bold tracking-[0.25em] uppercase">One-line pitch</div>
            <div className="font-heading text-2xl font-bold leading-snug mt-1.5">&ldquo;{memo.one_line_pitch}&rdquo;</div>
          </div>

          {/* FORMAT */}
          <div className="mt-7">
            <SectionTitle>Format</SectionTitle>
            <p className="text-[15px] leading-relaxed mt-2">{memo.format_description}</p>
            <div className="flex items-start justify-between gap-1 mt-6" data-testid="memo-format-steps">
              {(memo.format_steps || []).map((s, i, arr) => (
                <React.Fragment key={`${s.title}-${i}`}>
                  <div className="flex flex-col items-center text-center flex-1 min-w-0">
                    <div className="w-16 h-16 rounded-full border border-[#1a1812]/50 flex items-center justify-center">
                      <MIcon name={s.icon} className="w-7 h-7" />
                    </div>
                    <div className="text-[13px] font-semibold mt-2.5 leading-tight">{s.title}</div>
                    <div className="text-[12px] text-[#1a1812]/65 leading-tight mt-0.5">{s.subtitle}</div>
                  </div>
                  {i < arr.length - 1 && <ArrowRight className="w-4 h-4 mt-6 shrink-0 text-[#1a1812]/50" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* ===== Right: packshot ===== */}
        <div className="lg:col-span-2 flex flex-col">
          <div className="font-script text-2xl text-[#1a1812]/80 text-right pr-3 leading-none">
            {memo.annotation} <span className="inline-block -rotate-45">→</span>
          </div>
          <div className="mt-3 aspect-[4/5] bg-[#e7e3d6] overflow-hidden relative flex items-center justify-center" data-testid="memo-packshot">
            {packshotUrl ? (
              <img src={packshotUrl} alt={memo.product_name} className="w-full h-full object-cover" data-testid="memo-packshot-img" />
            ) : packshotLoading ? (
              <div className="flex flex-col items-center gap-3 text-[#1a1812]/60" data-testid="memo-packshot-loading">
                <Loader2 className="w-6 h-6 animate-spin" />
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase">Rendering packshot · gpt-image</div>
              </div>
            ) : (
              /* CSS sachet fallback */
              <div className="w-3/5 aspect-[3/4] bg-[#dcd8c4] border border-[#1a1812]/20 shadow-sm flex flex-col items-center justify-center p-4 text-center">
                <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#1a1812]/70">{memo.brand}</div>
                <div className="font-heading font-black uppercase text-2xl leading-none mt-3">{memo.product_name}</div>
                <div className="w-8 border-t border-[#1a1812]/40 my-3" />
                <div className="text-[10px] text-[#1a1812]/60">{memo.category}</div>
              </div>
            )}
          </div>
          <div className="flex justify-between font-mono text-[11px] mt-2.5 text-[#1a1812]/70">
            <span>{memo.pack_size}</span>
            <span className="font-bold text-[#1a1812]">₹{memo.price_inr}</span>
          </div>
        </div>
      </div>

      {/* ===== HOW IT WORKS / INGREDIENTS / CLEAN ===== */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10 pt-8 border-t border-[#1a1812]/25">
        <div>
          <SectionTitle>How it works</SectionTitle>
          <p className="text-[14px] leading-relaxed mt-3">{memo.how_it_works}</p>
        </div>
        <div>
          <SectionTitle>Key ingredients</SectionTitle>
          <ul className="mt-3 space-y-2.5" data-testid="memo-ingredients">
            {(memo.key_ingredients || []).map((ing, i) => (
              <li key={`${ing.name}-${i}`} className="text-[14px]">
                <span className="font-bold">· {ing.name}</span>
                <div className="text-[13px] text-[#1a1812]/70 ml-3">{ing.benefit}</div>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-[#eceadd] p-5" data-testid="memo-clean-box">
          <div className="font-heading font-black uppercase text-sm tracking-tight">Clean. Conscious. Effective.</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-5 mt-5">
            {(memo.clean_badges || []).map((b, i) => (
              <div key={`${b.label}-${i}`} className="flex flex-col items-center text-center gap-1.5">
                <div className="w-11 h-11 rounded-full border border-[#1a1812]/45 flex items-center justify-center">
                  <MIcon name={b.icon} className="w-5 h-5" />
                </div>
                <div className="text-[11px] font-medium">{b.label}</div>
              </div>
            ))}
          </div>
          {memo.clean_footer?.length > 0 && (
            <div className="flex justify-between border-t border-[#1a1812]/25 mt-5 pt-3 text-[11px] font-bold">
              {memo.clean_footer.slice(0, 2).map((c, i) => <span key={`${c}-${i}`}>{c}</span>)}
            </div>
          )}
        </div>
      </div>

      {/* ===== RESULTS + REAL LIFE ===== */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mt-9 items-start">
        <div className="md:col-span-2 bg-[#e9e7c4] p-5" data-testid="memo-results">
          <div className="font-heading font-black uppercase text-sm tracking-tight">Results you can feel</div>
          <ul className="mt-3.5 space-y-2">
            {(memo.results || []).map((r, i) => (
              <li key={`${r}-${i}`} className="flex items-start gap-2 text-[13px]">
                <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={2.5} />
                {r}
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <SectionTitle>Made for real life</SectionTitle>
          <div className="grid grid-cols-5 gap-2 mt-5" data-testid="memo-real-life">
            {(memo.real_life || []).map((m, i) => (
              <div key={`${m.title}-${i}`} className="flex flex-col items-center text-center">
                <MIcon name={m.icon} className="w-8 h-8 text-[#1a1812]/80" />
                <div className="text-[12px] font-bold mt-2.5 leading-tight">{m.title}</div>
                <div className="text-[11px] text-[#1a1812]/65 leading-tight mt-0.5">{m.subtitle}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== Cited internal signals ===== */}
      {memo.cited_signals?.length > 0 && (
        <div className="mt-9 pt-4 border-t border-[#1a1812]/25" data-testid="memo-cited-signals">
          <div className="font-mono text-[10px] font-bold tracking-[0.25em] uppercase">Grounded in live Black Mirror signals</div>
          <div className="flex flex-wrap gap-2 mt-2.5">
            {memo.cited_signals.map((t, i) => (
              <button key={`${t}-${i}`} onClick={() => navigate(`/signals?title=${encodeURIComponent(t)}`)}
                data-testid={`cited-signal-chip-${i}`}
                className="text-[11px] font-mono px-2 py-1 border border-[#1a1812]/30 bg-[#eceadd] hover:bg-[#1a1812] hover:text-[#f4f1e8] transition-colors cursor-pointer">
                ◈ {t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ===== Footer ===== */}
      <div className="mt-10 pt-4 border-t-2 border-[#1a1812] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="font-heading font-black uppercase tracking-tight text-lg">{memo.brand}</span>
          <span className="w-px h-4 bg-[#1a1812]/40" />
          <span className="text-xs text-[#1a1812]/70">{memo.tagline}</span>
        </div>
        <div className="flex items-center gap-6 text-xs">
          <span>{memo.closing_line}</span>
          <span className="font-bold">{memo.hashtag}</span>
        </div>
      </div>
    </div>
  );
};
